import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { httpClient } from "@/shared/api/http-client";
import type {
  AdminChannel,
  AdminCommunication,
  CreateChannelInput,
  ToggleChannelInput,
  UpdateConfigInput,
} from "../shared/communications-types";
import { findCommunication } from "../shared/communication-selectors";
import { buildCommunicationsQuery, type CommunicationsFilters } from "./build-communications-query";

export const COMMUNICATIONS_QUERY_KEY = ["communications", "list"] as const;

async function fetchCommunications(
  filters: CommunicationsFilters,
): Promise<readonly AdminCommunication[]> {
  return httpClient<readonly AdminCommunication[]>(
    `/api/admin/communications${buildCommunicationsQuery(filters)}`,
  );
}

/** Catalog list. Filters are part of the cache key so each filter combo
 *  is cached independently. */
export function useCommunications(filters: CommunicationsFilters = {}) {
  return useQuery({
    queryKey: [
      ...COMMUNICATIONS_QUERY_KEY,
      filters.key ?? "",
      filters.channel ?? "all",
      filters.locale ?? "all",
    ] as const,
    queryFn: () => fetchCommunications(filters),
    staleTime: 30_000,
  });
}

/**
 * Detail/editor data source. There is no `GET /communications/:id` on the
 * API (US-6.5 ships only the list), so the detail selects from the full
 * unfiltered list cache by id — one fetch backs the detail page, every
 * channel card, and the translation editor.
 */
export function useCommunicationDetail(id: string | undefined) {
  const query = useCommunications();
  return {
    ...query,
    communication: findCommunication(query.data, id),
  };
}

interface UpdateConfigParams {
  readonly id: string;
  readonly input: UpdateConfigInput;
}

async function updateConfig({ id, input }: UpdateConfigParams): Promise<AdminCommunication> {
  return httpClient<AdminCommunication>(`/api/admin/communications/${id}`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
}

export function useUpdateCommunicationConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COMMUNICATIONS_QUERY_KEY });
    },
  });
}

interface ToggleChannelParams {
  readonly channelId: string;
  readonly input: ToggleChannelInput;
}

async function toggleChannel({ channelId, input }: ToggleChannelParams): Promise<AdminChannel> {
  return httpClient<AdminChannel>(`/api/admin/communications/channels/${channelId}`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
}

export function useToggleChannel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: toggleChannel,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COMMUNICATIONS_QUERY_KEY });
    },
  });
}

interface CreateChannelParams {
  readonly communicationId: string;
  readonly input: CreateChannelInput;
}

/** Add a channel type to a communication — or revive a soft-deleted one
 *  (the API returns the same row, keeping its translations). */
async function createChannel({
  communicationId,
  input,
}: CreateChannelParams): Promise<AdminChannel> {
  return httpClient<AdminChannel>(`/api/admin/communications/${communicationId}/channels`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
}

export function useCreateChannel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createChannel,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COMMUNICATIONS_QUERY_KEY });
    },
  });
}

/** Soft-delete a channel — it disappears from the list but is revivable by
 *  adding the same type again. Distinct from toggling `isActive` (which keeps
 *  the channel visible but off). */
async function deleteChannel(channelId: string): Promise<AdminChannel> {
  return httpClient<AdminChannel>(`/api/admin/communications/channels/${channelId}`, {
    method: "DELETE",
  });
}

export function useDeleteChannel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteChannel,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COMMUNICATIONS_QUERY_KEY });
    },
  });
}
